/**
 * Manages the active room STOMP subscription for ChatContainer.
 *
 * Only one /topic/room/{roomId} subscription is active at a time.
 * The global user topic subscription lives in SocketProvider.
 */
import { useEffect, useRef } from "react";
import { useRecoilCallback, useRecoilValue } from "recoil";
import { userDetails } from "../../../components/recoil/userDataRecoil";
import socketService from "../../../socket/socketService";
import { SOCKET_TOPICS } from "../../../socket/socketConfig";
import {
  createRefreshChatRooms,
  handleRoomTopicEvent,
} from "../../../socket/socketEventHandlers";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import {
  CHAT_SOCKET_STATUS,
  chatCommunicationPageActiveState,
  chatSocketConnectedState,
  chatUnreadCountsState,
  selectedChatRoomIdState,
} from "../state/chatState";

const useActiveRoomSubscription = () => {
  const selectedRoomId = useRecoilValue(selectedChatRoomIdState);
  const socketStatus = useRecoilValue(chatSocketConnectedState);
  const isCommunicationPageActive = useRecoilValue(
    chatCommunicationPageActiveState,
  );
  const organizationIdentifier = useGetOrgIdentifier();
  const user = useRecoilValue(userDetails);
  const unsubscribeRef = useRef(null);

  const handleRoomEvent = useRecoilCallback(
    ({ snapshot, set }) =>
      (rawBody, roomId) => {
        const refreshRooms = createRefreshChatRooms(
          organizationIdentifier,
          user?.userIdentifier,
        );

        handleRoomTopicEvent(rawBody, roomId, {
          get: (atom) => snapshot.getLoadable(atom).contents,
          set,
          refreshRooms: () => refreshRooms(set),
        });
      },
    [organizationIdentifier, user?.userIdentifier],
  );

  const clearUnreadForRoom = useRecoilCallback(
    ({ set }) =>
      (roomId) => {
        if (!roomId) {
          return;
        }

        set(chatUnreadCountsState, (prev) => {
          if (!prev[String(roomId)]) {
            return prev;
          }

          const next = { ...prev };
          delete next[String(roomId)];
          return next;
        });
      },
    [],
  );

  useEffect(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;

    if (
      !selectedRoomId ||
      !isCommunicationPageActive ||
      socketStatus !== CHAT_SOCKET_STATUS.CONNECTED
    ) {
      return undefined;
    }

    const roomId = String(selectedRoomId);
    const destination = SOCKET_TOPICS.room(roomId);

    unsubscribeRef.current = socketService.subscribe(destination, (rawBody) => {
      handleRoomEvent(rawBody, roomId);
    });

    clearUnreadForRoom(roomId);

    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [
    clearUnreadForRoom,
    handleRoomEvent,
    isCommunicationPageActive,
    selectedRoomId,
    socketStatus,
  ]);
};

export default useActiveRoomSubscription;
